import axios from 'axios'
import { Toast } from 'react-vant'

// 创建axios实例
const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 10000,
})

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add trailing slash was removed because it causes issues with routes defined without slash
    // and leads to 401 errors during redirects.
    /*
    if (config.url) {
      const hasFileExtension = /\.[a-zA-Z0-9]+(\?|$)/.test(config.url);
      if (!hasFileExtension) {
        config.url = config.url.replace(/\/?(\?.*)?$/, '/$1');
      }
    }
    */

    const currentYear = localStorage.getItem('current_academic_year')
    if (currentYear) {
      const url = config.url || ''
      const isBlacklisted = ['/auth/', '/academic-year/', '/maintenance/', '/health/', '/upload/', '/notifications/', '/messages/', '/sports-meets/'].some(pattern => url.includes(pattern))
      if (!isBlacklisted) {
        config.headers['X-Academic-Year'] = encodeURIComponent(currentYear)
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
// 权限编码到友好中文名称的映射字典
const PERMISSION_LABEL_MAP = {
  'physical_test_read': '查看体测数据与大盘',
  'physical_test_create': '录入体测成绩',
  'physical_test_update': '修改体测成绩',
  'physical_test_delete': '删除体测记录',
  'physical_test_approve': '审核体测成绩',
  'physical_test_export': '体测报表与大盘导出',
  'student_read': '查看学生档案',
  'student_create': '新建学生档案',
  'student_update': '修改学生信息',
  'student_delete': '删除学生档案',
  'student_import': '批量导入学生',
  'student_export': '导出学生名单',
  'student_manage': '学生综合管理',
  'class_read': '查看班级列表',
  'class_create': '创建班级',
  'class_update': '修改班级信息',
  'class_delete': '解散/删除班级',
  'class_assign_students': '班级分班调班',
  'class_manage': '班级综合管理',
  'teacher_read': '查看教师名单',
  'teacher_manage': '教师分工与排课设置',
  'user_read': '查看用户列表',
  'user_create': '创建系统用户',
  'user_update': '修改用户信息',
  'user_delete': '注销系统用户',
  'user_manage': '用户账号管理',
  'role_manage': '角色与权限配置',
  'sports_meet_read': '查看运动会',
  'sports_meet_create': '创建运动会',
  'sports_meet_update': '编辑运动会项目',
  'sports_meet_delete': '删除运动会',
  'sports_meet_manage': '运动会综合编排与管理',
  'schedule_read': '查看课表',
  'schedule_manage': '智能排课与调课管理',
  'academic_read': '查看文化课成绩',
  'academic_create': '录入文化课成绩',
  'academic_manage': '学术成绩综合管理'
}

request.interceptors.response.use(
  (response) => {
    const { data } = response
    // 如果后端返回了标准的 { code, data, message } 格式
    if (data.code !== undefined && data.code !== 200) {
      Toast.info(data.message || '请求失败')
      return Promise.reject(new Error(data.message))
    }
    // 否则直接返回数据（适配 FastAPI 直接返回模型的情况）
    return data
  },
  (error) => {
    const { response } = error

    if (response?.status === 401) {
      // 核心修正：裁判口令访问和公开直播页面不强制跳转登录
      const isRefereeFlow = window.location.pathname.includes('/sports/referee');
      const isLivePage = window.location.pathname.includes('/sports/live');
      const isLoginRequest = error.config?.url?.includes('/auth/login');

      if (isRefereeFlow || isLivePage || isLoginRequest) {
        return Promise.reject(error)
      }
      Toast.fail('登录已过期，请重新登录')
      localStorage.removeItem('token')
      window.location.href = '/login'
    } else if (response?.status === 403) {
      // 优雅拦截 403 权限错误，提示联系管理员指派角色
      const payload = response?.data
      let rawDetail = payload?.message || payload?.detail || ''
      if (typeof rawDetail !== 'string') {
        rawDetail = Array.isArray(rawDetail) ? rawDetail.map(d => d.msg || d).join('；') : ''
      }
      const match = rawDetail.match(/需要权限:\s*([a-zA-Z0-9_,\s]+)/)
      let permNotice = '暂无操作权限，请联系管理员开通对应角色'
      if (match && match[1]) {
        const perms = match[1].split(',').map(s => s.trim()).filter(Boolean)
        const translated = perms.map(p => PERMISSION_LABEL_MAP[p] || p)
        permNotice = `暂无【${translated.join(' / ')}】权限，请联系管理员分配角色`
      }
      Toast.fail(permNotice)
    } else if (response?.status === 503 && response.data?.maintenance) {
      // 触发系统维护全局事件
      window.dispatchEvent(new CustomEvent('SYSTEM_MAINTENANCE', {
        detail: { message: response.data.message }
      }));
    } else if (response?.status === 543 || (response?.status >= 500 && !response.data?.message)) {
      // 处理 543 或其他无明确消息的服务器大错误，视为疑似维护
      window.dispatchEvent(new CustomEvent('SYSTEM_MAINTENANCE', {
        detail: { message: '服务器连接异常，可能处于维护中或代理线路繁忙。' }
      }));
    } else if (response?.status >= 500) {
      Toast.fail(response.data?.message || '服务器繁忙，请稍后再试')
    } else {
      // FastAPI 的错误体是 { detail }，422 时 detail 是数组；两种都要能读懂，
      // 否则像「口令对但这届运动会已归档」这种 409 只会显示成「网络错误」
      const payload = response?.data
      let msg = payload?.message
      if (!msg && payload?.detail) {
        msg = typeof payload.detail === 'string'
          ? payload.detail
          : (Array.isArray(payload.detail) ? payload.detail.map(d => d.msg || d).join('；') : '')
      }
      error.detail = msg
      Toast.fail(msg || '网络错误')
    }

    return Promise.reject(error)
  }
)

export default request
